import React from "react";
import { forkedRef } from "../utils/forkedRef";
import { FatButtonInputRightSideProp, FatButtonInputWrapper, LooseCheckboxInput } from "../__Internal__";

export type InternalCheckboxButtonProps = {
  id: string;
  name: string;
  value: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  rightSide?: FatButtonInputRightSideProp;
  extension?: React.ReactNode;
  icon?: string;
  disabled?: boolean;
  required?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  horizontal?: boolean;
  invalid?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onInvalid?: React.FormEventHandler<HTMLInputElement>;
  UNSAFE_flatTop?: boolean;
  "data-testid"?: string;
  compact?: boolean;
};

export const LooseCheckboxButton = React.forwardRef<HTMLInputElement, InternalCheckboxButtonProps>((props, forwardedRef) => {
  const {
    id,
    name,
    value,
    title,
    onChange,
    onInvalid,
    icon = null,
    subtitle = null,
    rightSide = null,
    extension = null,
    invalid = false,
    checked = false,
    indeterminate = false,
    disabled = false,
    horizontal = false,
    required = false,
    UNSAFE_flatTop = false,
    compact = false,
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);

  const testId = props["data-testid"] ?? "checkbox-button";

  /**
   * React doesn't support "indeterminate" state for checkboxes, so we have to do it manually.
   */
  React.useEffect(() => {
    if (inputRef.current && indeterminate !== undefined) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [inputRef.current, indeterminate, checked]);

  return (
    <FatButtonInputWrapper
      ref={forkedRef(forwardedRef, inputRef)}
      id={id}
      checked={checked}
      horizontal={horizontal}
      disabled={disabled}
      invalid={invalid}
      value={value}
      title={title}
      subtitle={subtitle}
      rightSide={rightSide}
      extension={extension}
      icon={icon}
      flatTopLeft={UNSAFE_flatTop}
      flatTopRight={UNSAFE_flatTop}
      compact={compact}
    >
      {(childProps) => (
        <LooseCheckboxInput
          {...childProps}
          name={name}
          required={required}
          onChange={onChange}
          onInvalid={onInvalid}
          data-testid={testId}
        />
      )}
    </FatButtonInputWrapper>
  );
});

LooseCheckboxButton.displayName = "InternalCheckboxButton";
